import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import capitalize from '@mui/material/utils/capitalize';
import { AssetData, TestData } from 'shared/hooks/useAssetData/types';

const EXCLUDE_META_FIELDS = ['subscribers', 'platform'];
const EXECUTABLE_ARTIFACT_TYPES = ['model', 'connector'];
const SUMMARY_EXCLUDE_ASSET_TYPES = ['EXPOSURE', 'ETL'];

const useAssetDetailsSectionData = ({
  asset,
  testData,
}: {
  asset?: AssetData;
  testData?: TestData;
}) => {
  const hasPermission = useEnvPermission();

  const assetId = asset?.id || '';

  const isCriticalAssetsIds = assetId && !testData ? [assetId] : [];

  const description =
    asset?.description?.trim() || testData?.description?.trim();

  const status = asset?.latestExecution?.status || testData?.status || '';

  const latestExecution =
    asset?.latestExecution?.executionTimestamp || testData?.executionTimestamp;

  const shouldShowLastRun =
    EXECUTABLE_ARTIFACT_TYPES.includes(asset?.artifactType || '') || !!testData;

  const path =
    asset?.type === 'EXPOSURE' ? asset?.fqn : asset?.path || testData?.path;

  const ymlPath = asset?.configPath;

  const subscribers: string[] = asset?.subscribers
    ? Array.isArray(asset.subscribers)
      ? asset.subscribers
      : [asset.subscribers]
    : [];

  const showSummaryButtons =
    !!asset &&
    !SUMMARY_EXCLUDE_ASSET_TYPES.includes(asset.type || '') &&
    hasPermission('VIEW_TESTS');

  const testExecutionsByQualityDimension =
    asset?.testExecutionsByQualityDimension;

  const metaFields = Object.entries(asset?.additionalProperties || {})
    .filter(([key]) => !EXCLUDE_META_FIELDS.includes(key))
    .map(([key, value]) => ({
      title: capitalize(key.replaceAll('_', ' ')),
      type: Array.isArray(value)
        ? 'list'
        : typeof value === 'boolean'
        ? 'bool'
        : typeof value === 'object'
        ? 'object'
        : 'text',
      value,
    }));

  return {
    isTest: !!testData,
    assetId,
    isCriticalAssetsIds,
    description,
    status,
    latestExecution,
    shouldShowLastRun,
    path,
    ymlPath,
    subscribers,
    showSummaryButtons,
    testExecutionsByQualityDimension,
    owners: asset?.owners || [],
    metaFields,
  };
};

export default useAssetDetailsSectionData;
